import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MaintainTabDTO, TickerConfigDTO } from '../../../../../system/system-dtos';
import { ActiveSort } from './main-table-content-v2.component';
import { DataType } from '../../../../data-type-widgets/data-type/data-type-component';
import { DataTypeComparatorsService } from '../../../../data-type-widgets/data-type/services/data-type-comparators.service';
import { FilterParams } from '../../dto/filter-dtos';
import { LockService } from '../../service/lock-service';

export class TickerTableDataSource {
  private comparatorsService: DataTypeComparatorsService;

  public tableConfig: TickerConfigDTO;

  public data: any[] = [];

  public filteredData: any[];

  public pageData: any[];

  public filterPredicate: (data: any, filter: FilterParams) => boolean = this.getDefaultFilterPredicate();

  public _tabSelected: MaintainTabDTO;

  private _paginator;

  private activeSort = null;

  public sortSubject: Subject<ActiveSort> = new BehaviorSubject(null);

  private activeFilter = null;

  public filterSubject: Subject<FilterParams> = new BehaviorSubject(null);

  public pageDataSubject: Subject<any[]> = new BehaviorSubject<any[]>([]);

  private paginatorSubscription: Subscription;

  private filterSubscription: Subscription;

  private sortSubscription: Subscription;

  private lockService: LockService;

  public changeSubject: Subject<any[]> = new BehaviorSubject([]);

  constructor(lockService: LockService, tableConfig: TickerConfigDTO, data: any[]) {
    this.lockService = lockService;
    this.reload(tableConfig, data);
  }

  public reload(tableConfig: TickerConfigDTO, data: any[]) {
    this.comparatorsService = new DataTypeComparatorsService();
    this.tableConfig = tableConfig;
    this.data = data;
    this.filteredData = this.data;
    this.tabSelected = this.tableConfig.tabs[0];

    this.filterSubscription = this.filterSubject
      .pipe(map((d) => d || { text: '', locksOnly: false, cohortId: undefined }))
      .subscribe((filterValue) => {
        this.activeFilter = filterValue;
        this.filteredData = this.data
          .filter((entry) => this.filterByTabSelected(entry))
          .filter((entry) => this.filterPredicate(entry, filterValue));
        if (this.activeSort) {
          this.filteredData = this.doSort(this.activeSort, this.filteredData);
        }
        this.rebuildPageData();
      });

    this.sortSubscription = this.sortSubject.pipe(filter((d) => !!d)).subscribe((activeSort: ActiveSort) => {
      this.activeSort = activeSort;
      this.filteredData = this.doSort(activeSort, this.filteredData);
      this.rebuildPageData();
    });

    this.changeSubject.next(data);
  }

  private rebuildPageData(event?: PageEvent) {
    if (!this.paginator) {
      return;
    }
    this.paginator.length = this.filteredData.length;
    const pageIndex = event?.pageIndex || this.paginator.pageIndex;
    const pageSize = event?.pageSize || this.paginator.pageSize;

    let start = pageIndex * pageSize;
    const end = (pageIndex + 1) * pageSize;
    if (start > this.paginator.length) {
      this.paginator.pageIndex = 0;
      start = 0;
    }
    this.pageData = this.filteredData.slice(start, end);
    this.sendPageDataEvent();
  }

  private doSort(activeSort: ActiveSort, array: any[]): any[] {
    const field = activeSort.fieldConfig;
    const comparator = this.comparatorsService.getComparator(DataType[field.type]);
    const directionFunction: (number) => number = activeSort.direction === 'ASC' ? (x) => x : (x) => -x;
    return array.sort((el1, el2) => directionFunction(comparator(el1.data[field.name], el2.data[field.name])));
  }

  private getDefaultFilterPredicate() {
    return (data, filter) =>
      this.filterByText(data, filter.text) &&
      this.filterByLocks(data, filter.locksOnly) &&
      this.filterByCohort(data, filter.cohortId);
  }

  private filterByText(data: any, filterText: string) {
    if (filterText === '') {
      return true;
    }
    const valueStr = this.getObjectValuesAsString(data).toLowerCase().trim();
    const actualData = data.data;
    for (let filterPart of filterText.split(',')) {
      if (filterPart.includes(':')) {
        // key:value
        const fitlerKeyAndValue = filterPart.split(':');
        const filterKey = fitlerKeyAndValue[0].trim();
        const filterValue = fitlerKeyAndValue[1].trim();
        const value = actualData[filterKey];
        if (
          value === undefined ||
          !String(value).toLowerCase().trim().includes(String(filterValue).toLowerCase().trim())
        ) {
          return false;
        }
      } else if (!valueStr.includes(filterPart.toLowerCase().trim())) {
        // plain text
        return false;
      }
    }
    return true;
  }

  private getObjectValuesAsString(data: any): string {
    let valueStr = '';
    const actualData = data.data;
    Object.keys(actualData).forEach((k) => {
      const value = actualData[k];
      if (value) {
        valueStr += String(value);
        Object.keys(value).forEach((k2) => (valueStr += String(value[k2])));
      }
    });
    return valueStr;
  }

  get paginator(): MatPaginator | null {
    return this._paginator;
  }

  set paginator(paginator: MatPaginator | null) {
    this._paginator = paginator;
    this.rebuildPageData();

    this.paginatorSubscription = paginator.page.subscribe((e) => {
      this.rebuildPageData(e);
    });
  }

  addElement(el: any) {
    const mainIndex = this.data.findIndex((d) => el.id === d.data.id);
    if (mainIndex < 0) {
      this.data.push(el);
      this.addElementToFilteredData(el);
    }
  }

  addElementToFilteredData(el: any) {
    let filteredDataCopy = this.filteredData.slice();
    filteredDataCopy.push(el);
    if (this.activeSort) {
      filteredDataCopy = this.doSort(this.activeSort, filteredDataCopy);
    }
    if (this.activeFilter) {
      filteredDataCopy = filteredDataCopy.filter((entry) => this.filterPredicate(entry, this.activeFilter));
    }
    const filteredIndex = filteredDataCopy.findIndex((d) => el.id === d.data.id);
    if (filteredIndex >= 0) {
      this.filteredData.splice(filteredIndex, 0, el);
      this.rebuildPageData();
    }
  }

  updateElement(el: any) {
    const mainIndex = this.data.findIndex((d) => el.id === d.data.id);
    if (mainIndex >= 0) {
      el.checked = this.data[mainIndex].checked;
      el.expanded = this.data[mainIndex].expanded;
      this.data.splice(mainIndex, 1, el);
    }

    const filteredIndex = this.filteredData.findIndex((d) => el.id === d.data.id);
    const meetsFilterCriteria = !this.activeFilter || this.filterPredicate(el, this.activeFilter);

    if (filteredIndex < 0 && meetsFilterCriteria) {
      this.addElementToFilteredData(el);
      return;
    }

    if (filteredIndex >= 0) {
      if (meetsFilterCriteria) {
        this.filteredData.splice(filteredIndex, 1, el);
      } else {
        this.filteredData.splice(filteredIndex, 1);
      }
    }

    const pageIndex = this.pageData.findIndex((d) => el.id === d.data.id);
    if (pageIndex >= 0) {
      if (meetsFilterCriteria) {
        this.pageData.splice(pageIndex, 1, el);
      } else {
        this.pageData.splice(pageIndex, 1);
      }
      this.rebuildPageData();
    }
  }

  removeElement(el: any) {
    const filteredIndex = this.filteredData.findIndex((d) => el.id === d.data.id);
    if (filteredIndex >= 0) {
      this.filteredData.splice(filteredIndex, 1);
    }
    const mainIndex = this.data.findIndex((d) => el.id === d.data.id);
    if (mainIndex >= 0) {
      this.data.splice(mainIndex, 1);
    }
    const pageIndex = this.data.findIndex((d) => el.id === d.data.id);
    if (pageIndex >= 0) {
      this.data.splice(pageIndex, 1);
    }
    this.rebuildPageData();
  }

  sendPageDataEvent() {
    this.pageDataSubject.next(this.pageData);
  }

  checkAllOnPage() {
    this.pageData.forEach((d) => (d.checked = true));
    this.sendPageDataEvent();
  }

  uncheckAllOnPage() {
    this.pageData.forEach((d) => (d.checked = false));
    this.sendPageDataEvent();
  }

  get tabSelected(): MaintainTabDTO | null {
    return this._tabSelected;
  }

  set tabSelected(tab: MaintainTabDTO | null) {
    this._tabSelected = tab;
    this.filterSubject.next(this.activeFilter);
  }

  private filterByLocks(entry: any, locksOnly: boolean): boolean {
    return !locksOnly || this.lockService.isEntryLockedByCurrentAdmin(entry);
  }

  private filterByCohort(entry: any, cohortId: number): boolean {
    return !cohortId || !entry.data.cohort || entry.data.cohort.id === cohortId;
  }

  private filterByTabSelected(entry: any): boolean {
    if (this?.tabSelected?.initialConditions?.length > 0) {
      const { initialConditions } = this.tabSelected;
      return initialConditions.filter((c) => entry.data[c.field] === c.value).length === initialConditions.length;
    }
    return true;
  }

  public close() {
    this.filterSubscription.unsubscribe();
    this.sortSubscription.unsubscribe();
    this.paginatorSubscription.unsubscribe();
  }
}
